import { useState, useMemo, useEffect } from "react";
import { MaterialReactTable } from "material-react-table";
import { Box, Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ExportToCsv } from "export-to-csv";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../utils/authProvider";
import "../MRT/MRTStyle.css";

const MRT = ({ ddInfo }) => {
  const navigate = useNavigate();
  const { getAuth, refreshCheck } = useAuthContext();

  const [xref, setXref] = useState([]);
  const [updatedColumns, setUpdatedColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  const columns = useMemo(
    () => [
      {
        accessorKey: "siteId",
        header: "Site ID",
        size: 120,
      },
      {
        accessorKey: "date",
        header: "Date",
        size: 120,
        sortingFn: (rowA, rowB, columnId) => {
          const a = new Date(rowA.getValue(columnId));
          const b = new Date(rowB.getValue(columnId));
          return a - b;
        },
      },
      { accessorKey: "userRef", header: "User Ref", size: 120 },
      { accessorKey: "siteName", header: "Site Name", size: 120 },
      { accessorKey: "hoursOnline", header: "Hours On", size: 120 },
      { accessorKey: "hoursDown", header: "Hours Down", size: 120 },
      { accessorKey: "downCode", header: "Down Code", size: 120 },
      {
        accessorKey: "downcodeDescription",
        header: "Down Code Desc",
        size: 120,
      },
      { accessorKey: "grossOil", header: "Gross Oil", size: 120 },
      { accessorKey: "grossGas", header: "Gross Gas", size: 120 },
      { accessorKey: "grossWater", header: "Gross Water", size: 120 },
      { accessorKey: "netOil", header: "Net Oil", size: 120 },
      { accessorKey: "netGas", header: "Net Gas", size: 120 },
      { accessorKey: "netWater", header: "Net Water", size: 120 },
      { accessorKey: "injectedVolume", header: "Inj Vol", size: 120 },
      { accessorKey: "forecastOil", header: "Forecast Oil", size: 120 },
      { accessorKey: "forecastGas", header: "Forecast Gas", size: 120 },
      { accessorKey: "forecastWater", header: "Forecast Water", size: 120 },
      { accessorKey: "boe", header: "BOE", size: 120 },
      { accessorKey: "netBOE", header: "Net BOE", size: 120 },
      { accessorKey: "oilSales", header: "Oil Sales", size: 120 },
      { accessorKey: "gasSales", header: "Gas Sales", size: 120 },
      { accessorKey: "gasLift", header: "Gas Lift", size: 120 },
      { accessorKey: "leaseUse", header: "Lease Use", size: 120 },
      { accessorKey: "flare", header: "Flare", size: 120 },
      { accessorKey: "casingPressure", header: "Casing Prs", size: 120 },
      { accessorKey: "casingPressureMin", header: "Casing Prs Min", size: 120 },
      { accessorKey: "casingPressureMax", header: "Casing Prs Max", size: 120 },
      {
        accessorKey: "casingPressureInt",
        header: "Intermediate Casing Prs",
        size: 225,
      },
      {
        accessorKey: "casingPressureSrf",
        header: "Surface Casing Prs",
        size: 120,
      },
      {
        accessorKey: "casingPressureSi",
        header: "Shut-In Casing Prs",
        size: 120,
      },
      { accessorKey: "tubingPressure", header: "Tubing Prs", size: 120 },
      { accessorKey: "tubingPressureMin", header: "Tubing PrsMin", size: 120 },
      { accessorKey: "tubingPressureMax", header: "Tubing Prs Max", size: 120 },
      {
        accessorKey: "tubingPressureSi",
        header: "Shut-In Tubing Prs",
        size: 120,
      },
      { accessorKey: "linePressure", header: "Line Prs", size: 120 },
      { accessorKey: "pumpInletPressure", header: "Pump Inlet Prs", size: 120 },
      { accessorKey: "gasLiftPressure", header: "Gas Lift Prs", size: 120 },
      {
        accessorKey: "bradenheadPressure",
        header: "Bradenhead Prs",
        size: 120,
      },
      { accessorKey: "choke", header: "Choke", size: 120 },
      { accessorKey: "casingChoke", header: "Casing Choke", size: 120 },
      { accessorKey: "fluidLevel", header: "Fluid Level", size: 120 },
      { accessorKey: "gor", header: "GOR", size: 120 },
      { accessorKey: "wor", header: "WOR", size: 120 },
      { accessorKey: "mcfbbl", header: "MCF/BBL", size: 120 },
      { accessorKey: "mmcfbbl", header: "MMCF/BBL", size: 120 },
      { accessorKey: "waterCut", header: "Water Cut", size: 120 },
      { accessorKey: "oilCut", header: "Oil Cut", size: 120 },
      { accessorKey: "chartNote", header: "Chart Note", size: 120 },
      { accessorKey: "remarks", header: "Remarks", size: 120 },
      { accessorKey: "user_1", header: "User Data 1", size: 120 },
      { accessorKey: "user_2", header: "User Data 2", size: 120 },
      { accessorKey: "user_3", header: "User Data 3", size: 120 },
      { accessorKey: "user_4", header: "User Data 4", size: 120 },
      { accessorKey: "user_5", header: "User Data 5", size: 120 },
      { accessorKey: "user_6", header: "User Data 6", size: 120 },
      { accessorKey: "user_7", header: "User Data 7", size: 120 },
      { accessorKey: "user_8", header: "User Data 8", size: 120 },
      { accessorKey: "user_9", header: "User Data 9", size: 120 },
      { accessorKey: "nri", header: "NRI", size: 120 },
      { accessorKey: "average7Oil", header: "Avg 7 Day Oil", size: 120 },
      { accessorKey: "average7Gas", header: "Avg 7 Day Gas", size: 120 },
      { accessorKey: "average7Water", header: "Avg 7 Day Water", size: 120 },
    ],
    []
  );
  const [columnVisibility, setColumnVisibility] = useState({
    siteId: false,
    date: true,
    userRef: true,
    siteName: false,
    hoursOnline: true,
    hoursDown: true,
    downCode: false,
    downcodeDescription: false,
    grossOil: true,
    grossGas: true,
    grossWater: true,
    netOil: false,
    netGas: false,
    netWater: false,
    injectedVolume: true,
    forecastOil: false,
    forecastGas: false,
    forecastWater: false,
    boe: false,
    netBOE: false,
    oilSales: true,
    gasSales: true,
    gasLift: true,
    leaseUse: true,
    flare: true,
    casingPressure: false,
    casingPressureMin: false,
    casingPressureMax: false,
    casingPressureInt: false,
    casingPressureSrf: false,
    casingPressureSi: false,
    tubingPressure: false,
    linePressure: false,
    pumpInletPressure: false,
    gasLiftPressure: false,
    bradenheadPressure: false,
    choke: false,
    casingChoke: false,
    fluidLevel: false,
    gor: false,
    wor: false,
    mcfbbl: false,
    mmcfbbl: false,
    waterCut: false,
    oilCut: false,
    chartNote: false,
    remarks: false,
    user_1: false,
    user_2: false,
    user_3: false,
    user_4: false,
    user_5: false,
    user_6: false,
    user_7: false,
    user_8: false,
    user_9: false,
    nri: false,
    average7Oil: false,
    average7Gas: false,
    average7Water: false,
  });
  useEffect(() => {
    console.log("Column visibility changed:", columnVisibility);
  }, [columnVisibility]);

  useEffect(() => {
    getXref();
    console.log("mrt start");
  }, []);
  useEffect(() => {
    if (xref.length > 0) {
      // Update column headers based on xref data
      const updatedColumns = columns.map((column) => {
        const xrefItem = xref.find((x) => x.key === column.accessorKey);
        if (xrefItem) {
          return { ...column, header: xrefItem.value };
        }
        return column;
      });
      setUpdatedColumns(updatedColumns);
    }
  }, [xref]);
  useEffect(() => {
    if (updatedColumns.length > 0) {
      console.log("updatedColumns", updatedColumns);
      setLoading(false);
    }
    return;
  }, [updatedColumns]);

  const getXref = async () => {
    setLoading(true);
    const authT = await getAuth();
    const options = {
      method: "GET",
      url: "https://api.pramsplus.com/xref-labels",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authT}`,
      },
    };
    if (authT !== false) {
      try {
        const response = await axios.request(options);
        // console.log("xref data", response.data);

        setXref(response.data);
      } catch (err) {
        console.log(err.response.status);
        if (err.response.status === 401) {
          const valid = await refreshCheck("getXref");
          if (valid === true) {
            console.log("Retrying getXref api call");
            getXref();
          } else if (valid === false) {
            console.log("all retries failed getXref");
            navigate("/login");
          }
        } else {
          console.log("Redirecting to login something went wrong in getXref");
          navigate("/login");
        }
        return;
      }
    }
    return;
  };
  const handleExportRows = (rows, updatedColumns) => {
    const visibleColumns = updatedColumns.filter(
      (col) =>
        col.getIsVisible() &&
        col.id && // ignore columns without valid id
        col.columnDef.header !== "Select" // ignore MRT's checkbox column
    );
    // ? Use this to debug visible vs original keys
    // console.log(
    //   "Visible column keys:",
    //   visibleColumns.map((c) => c.columnDef.accessorKey)
    // );
    // console.log("Row original keys:", Object.keys(rows[0].original));

    const csvExporter = new ExportToCsv({
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: false,
      filename: `DataView_Export_${new Date().toISOString().slice(0, 10)}`,
      headers: visibleColumns.map((col) => col.columnDef.header ?? col.id),
    });

    const exportData = rows.map((row) =>
      visibleColumns.reduce((acc, col) => {
        const key = col.columnDef.accessorKey ?? col.id;
        const rawValue = row.original[key];
        acc[col.columnDef.header ?? key] =
          rawValue === null || rawValue === undefined || rawValue === ""
            ? "-"
            : rawValue;
        return acc;
      }, {})
    );

    csvExporter.generateCsv(exportData);
  };

  // const handleExportRows = (rows, updatedColumns) => {
  //   const visibleColumns = updatedColumns.filter(
  //     (col) =>
  //       col.getIsVisible() &&
  //       col.id && // ignore columns without valid id
  //       col.columnDef.header !== "Select" // ignore MRT's checkbox column
  //   );

  //   console.log(
  //     "visible columns",
  //     visibleColumns.map((c) => ({
  //       id: c.id,
  //       accessorKey: c.columnDef.accessorKey,
  //     }))
  //   );

  //   console.log("original", rows[0].original);

  //   const csvOptionsWithHeaders = {
  //     fieldSeparator: ",",
  //     quoteStrings: '"',
  //     decimalSeparator: ".",
  //     showLabels: true,
  //     useBom: true,
  //     useKeysAsHeaders: false,
  //     filename: `DataView_Export_${new Date().toISOString().slice(0, 10)}`,
  //     headers: visibleColumns.map((col) => col.columnDef.header),
  //   };
  //   const csvExporter = new ExportToCsv(csvOptionsWithHeaders);
  //   const exportData = rows.map((row) =>
  //     visibleColumns.reduce((acc, col) => {
  //       const rawValue = row.original[col.id];
  //       // const rawValue = row.getValue(col.id);
  //       acc[col.id] =
  //         rawValue === null || rawValue === undefined || rawValue === ""
  //           ? "-"
  //           : rawValue;
  //       return acc;
  //     }, {})
  //   );
  //   csvExporter.generateCsv(exportData);
  // };

  return (
    <>
      {loading ? null : (
        <MaterialReactTable
          columns={updatedColumns}
          data={ddInfo}
          initialState={{ density: "compact" }}
          state={{ columnVisibility }}
          onColumnVisibilityChange={setColumnVisibility}
          enableRowSelection
          enableStickyHeader
          enablePagination={false}
          enableBottomToolbar={false}
          positionToolbarAlertBanner="bottom"
          muiTableContainerProps={{ sx: { maxHeight: "325px", width: "100%" } }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                p: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <Button
                color="primary"
                onClick={() =>
                  handleExportRows(
                    table.getRowModel().rows,
                    table.getAllColumns()
                  )
                }
                startIcon={<FileDownloadIcon />}
                variant="contained"
              >
                Export Data
              </Button>
              <Button
                disabled={
                  !table.getIsSomeRowsSelected() &&
                  !table.getIsAllRowsSelected()
                }
                onClick={() =>
                  handleExportRows(
                    table.getSelectedRowModel().rows,
                    table.getAllColumns()
                  )
                }
                startIcon={<FileDownloadIcon />}
                variant="contained"
              >
                Export Selected Rows
              </Button>
            </Box>
          )}
        />
      )}
    </>
  );
};

export default MRT;
